import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Modal, Button, fmtInr, Label } from '../ui';
import { Picker } from './Picker';
import { LastPrices, lineMath, sumLines, emptyLine } from './LineItemsEditor';

export const CLOSE_WARNING = 'Close the form?\n\nAny unsaved data will be LOST.';

// Flow-style full-width document form: sectioned body, sticky footer, close guard.
export const DocForm = ({ open, onClose, dirty, title, subtitle, onSubmit, busy, submitLabel = 'Save', children, footerLeft, testid = 'docform', width = 'max-w-5xl' }) => {
  const requestClose = () => { if (!dirty || window.confirm(CLOSE_WARNING)) onClose(); };
  return (
    <Modal open={open} onClose={requestClose} title={title} subtitle={subtitle} width={width} testid={testid}
      footer={<div className="flex items-center justify-between w-full gap-4">
        <div className="text-sm">{footerLeft}</div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={requestClose} data-testid={`${testid}-cancel`}>Cancel</Button>
          <Button type="submit" form={`${testid}-form`} disabled={busy} data-testid={`${testid}-submit`}>{busy ? 'Saving…' : submitLabel}</Button>
        </div>
      </div>}>
      <form id={`${testid}-form`} onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-6">{children}</form>
    </Modal>
  );
};

export const Section = ({ title, hint, children, action }) => (
  <section>
    <div className="flex items-center justify-between mb-3">
      <div><h4 className="text-[13px] font-extrabold uppercase tracking-wider" style={{ color: 'var(--erp-navy)' }}>{title}</h4>{hint && <p className="text-xs" style={{ color: 'var(--erp-muted)' }}>{hint}</p>}</div>
      {action}
    </div>
    {children}
  </section>
);

const Num = ({ label, value, onChange, testid, ...p }) => (
  <div>
    <Label>{label}</Label>
    <input type="number" step="any" min={0} value={value} onChange={(e) => onChange(e.target.value)} className="erp-input !h-10 text-right" data-testid={testid} {...p} />
  </div>
);

// "Item 1 / Item 2" card blocks (Flow Management style).
export const ItemBlocks = ({ lines, onChange, skus, customerId, testid = 'items' }) => {
  const opts = skus.map((s) => ({ value: s.id, label: s.name, sub: `${s.code} · ${fmtInr(s.list_price)} / ${s.uom}` }));
  const upd = (i, patch) => onChange(lines.map((l, n) => (n === i ? { ...l, ...patch } : l)));
  const pick = (i, id) => { const s = skus.find((x) => x.id === id); upd(i, { sku_id: id, rate: lines[i].rate || s?.list_price || '', uom: s?.uom }); };
  return (
    <div className="space-y-3" data-testid={testid}>
      {lines.map((l, i) => {
        const m = lineMath(l); const sku = skus.find((s) => s.id === l.sku_id);
        return (
          <div key={l.key} className="rounded-xl border p-4" style={{ borderColor: 'var(--erp-line)', background: '#fafbfe' }} data-testid={`${testid}-block-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-extrabold tracking-wider" style={{ color: 'var(--erp-blue)' }}>ITEM {i + 1}</span>
              <div className="flex items-center gap-3">
                <span className="text-sm font-extrabold" data-testid={`${testid}-amount-${i}`}>{fmtInr(m.amount)}</span>
                <button type="button" disabled={lines.length === 1} onClick={() => onChange(lines.filter((_, n) => n !== i))} className="h-7 w-7 rounded-md grid place-items-center text-red-500 hover:bg-red-50 disabled:opacity-30" data-testid={`${testid}-remove-${i}`}><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 lg:col-span-5">
                <Label required>Item</Label>
                <Picker value={l.sku_id} onChange={(id) => pick(i, id)} options={opts} placeholder="Type to search SKU…" testid={`${testid}-sku-${i}`} required />
                {sku && <p className="text-[11px] mt-1" style={{ color: 'var(--erp-muted)' }}>{sku.code} · HSN {sku.hsn_code || '—'} · List {fmtInr(sku.list_price)}</p>}
                <LastPrices customerId={customerId} skuId={l.sku_id} />
              </div>
              <div className="col-span-4 lg:col-span-2"><Num label={`Qty${l.uom ? ` (${l.uom})` : ''}`} value={l.qty} onChange={(v) => upd(i, { qty: v })} testid={`${testid}-qty-${i}`} required /></div>
              <div className="col-span-4 lg:col-span-2"><Num label="Rate ₹" value={l.rate} onChange={(v) => upd(i, { rate: v })} testid={`${testid}-rate-${i}`} required /></div>
              <div className="col-span-2 lg:col-span-1"><Num label="Disc %" value={l.discount_pct} onChange={(v) => upd(i, { discount_pct: v })} testid={`${testid}-disc-${i}`} max={100} /></div>
              <div className="col-span-2 lg:col-span-1"><Num label="GST %" value={l.gst_rate} onChange={(v) => upd(i, { gst_rate: v })} testid={`${testid}-gst-${i}`} /></div>
              <div className="col-span-12 lg:col-span-1 flex lg:flex-col justify-end text-right text-[11px]" style={{ color: 'var(--erp-muted)' }}>
                <span>Taxable {fmtInr(m.taxable)}</span><span className="lg:mt-0.5 ml-3 lg:ml-0">Tax {fmtInr(m.tax)}</span>
              </div>
            </div>
          </div>
        );
      })}
      <Button variant="soft" size="sm" icon={Plus} onClick={() => onChange([...lines, emptyLine()])} data-testid={`${testid}-add`}>Add Item</Button>
    </div>
  );
};

export const TotalsBar = ({ lines }) => {
  const t = sumLines(lines);
  return (
    <div className="flex flex-wrap items-center gap-5" data-testid="form-totals">
      <span style={{ color: 'var(--erp-muted)' }}>Taxable <b style={{ color: 'var(--erp-ink)' }}>{fmtInr(t.taxable)}</b></span>
      <span style={{ color: 'var(--erp-muted)' }}>GST <b style={{ color: 'var(--erp-ink)' }}>{fmtInr(t.tax)}</b></span>
      <span className="text-base font-extrabold" style={{ color: 'var(--erp-navy)' }}>Total {fmtInr(Math.round(t.total))}</span>
    </div>
  );
};
